import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from './MyPrivateTutor.module.scss';
import classes from 'classnames';
import React from 'react';
import TutorReportModal from '@/components/TutorReportModal/TutorReportModal';
import {
  GetChatsPageResponse,
  GetGroupReportTopicsResponse,
} from '@mavatar/melingo-voice-and-avatar-sdk/dist/Tutor/EllReport';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import UserUtils from '@/utils/user';
import { TeacherGroup } from '@/types/user';
import { ClassOverviewData } from '@/types/progress';
import { PrivateTutorTypes } from '@/pages/dashboard/tutor-chat/TutorChat';
import SelfView, { SortData, sortOptions, sortMistakesOptions } from './SelfView';
import GroupView from './GroupView';
import UseScreenSize from '@/hooks/UseScreenSize';
import MobileMessage from '@/components/MobileMessage/MobileMessage';

const MY_DASHBORT_GROUP: TeacherGroup = {
  id: '',
  name: 'My Dashboard',
  grade: '',
  groupLevel: '',
  invitationLinkForStudents: '',
};

const MyPrivateTutor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = UseScreenSize();
  const [students, setStudents] = useState<{ [student_id: string]: ClassOverviewData }>({});
  const [report, setReport] = useState<
    GetChatsPageResponse | GetGroupReportTopicsResponse[] | null
  >(null);
  const [reportUserId, setReportUserId] = useState<string>('');
  const [showReport, setShowReport] = useState<boolean>(false);
  const { user } = useAppSelector(authSelector);
  const [selectedGroup, setSelectedGroup] = useState<TeacherGroup>(MY_DASHBORT_GROUP);
  const [showMobileClassMessage, setShowMobileClassMessage] = useState<boolean>(false);
  const [selectedSortData, setSelectedSortData] = useState<SortData>(sortOptions[1]);
  const [selectedMistakesSortData, setSelectedMistakesSortData] = useState<SortData>(
    sortMistakesOptions[2]
  );

  const isTeacher = useMemo(() => {
    return UserUtils.isTeacher(user);
  }, [user]);

  const onReportClose = useCallback(() => {
    setShowReport(false);
  }, []);

  const openReport = useCallback(
    (report: GetChatsPageResponse | GetGroupReportTopicsResponse[], userId: string) => {
      setReport(report);
      setReportUserId(userId);
      setShowReport(!!report);
    },
    []
  );

  const startConversation = useCallback(
    (chat: GetChatsPageResponse) => {
      navigate('/dashboard/my-tutor-chat', {
        state: {
          type: PrivateTutorTypes.START_OVER,
          levelId: chat?.levelId || 0,
          unitId: chat?.unitId || 0,
          topicId: chat?.topicId || 0,
          activityId: chat?.activityId || 0,
        },
      });
    },
    [navigate]
  );

  const teachersClasses = useMemo(() => {
    if (!isTeacher) {
      return;
    }

    const groups: TeacherGroup[] = [
      {
        ...MY_DASHBORT_GROUP,
        name: t('my_tutor_page.myDashboard'),
      },
      ...(user?.metadata?.organizationDetails?.teacher?.teacherGroups || []),
    ];

    return (
      <div className={classNames.teachersClasses}>
        <div className={classNames.teacherTabs}>
          {groups.map((group, groupIndex) => {
            const isActiveTab = selectedGroup.id === group.id;

            return (
              <button
                key={`teacher-group--${groupIndex}`}
                className={classes(classNames.cls, {
                  [classNames.activeTab]: isActiveTab,
                })}
                onClick={() => {
                  if (isMobile && group.id) {
                    setShowMobileClassMessage(true);
                  } else {
                    setSelectedGroup(group);
                  }
                }}
              >
                <div className={classNames.textWarrper}>
                  <span className={classNames.opacity}>{group.name}</span>
                  <span className={classNames.text}>{group.name}</span>
                </div>
                {isActiveTab && <div className={classNames.underLine}></div>}
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [isTeacher, user?.metadata?.organizationDetails?.teacher?.teacherGroups, selectedGroup, t]);

  const showView = useMemo(() => {
    const isGroupSelected = !!selectedGroup?.id;

    if (isGroupSelected) {
      return 'group';
    }

    return 'self';
  }, [selectedGroup]);

  const groupView = useMemo(() => {
    if (showView !== 'group') {
      return;
    }

    return (
      <GroupView
        teachersClasses={teachersClasses}
        selectedGroup={selectedGroup}
        students={students}
        setStudents={setStudents}
        openReport={openReport}
      />
    );
  }, [showView, teachersClasses, selectedGroup, students, openReport]);

  const selfView = useMemo(() => {
    if (showView !== 'self') {
      return;
    }

    return (
      <SelfView
        teachersClasses={teachersClasses}
        openReport={openReport}
        startConversation={startConversation}
        selectedSortData={selectedSortData}
        setSelectedSortData={setSelectedSortData}
        selectedMistakesSortData={selectedMistakesSortData}
        setSelectedMistakesSortData={setSelectedMistakesSortData}
        setStudents={setStudents}
      />
    );
  }, [
    showView,
    teachersClasses,
    selectedSortData,
    selectedMistakesSortData,
    setSelectedMistakesSortData,
    openReport,
    startConversation,
  ]);

  return (
    <>
      {groupView}
      {selfView}

      <TutorReportModal
        show={showReport}
        report={report}
        reportUserId={reportUserId}
        onClose={onReportClose}
        group={selectedGroup}
        studentsMapper={students}
      />

      <MobileMessage
        show={showMobileClassMessage}
        onClose={() => setShowMobileClassMessage(false)}
        message={t('teacherMobile.tutorMessage') || ''}
      />
    </>
  );
};

export default React.memo(MyPrivateTutor);

// get and render mistakesBlock
/*
  useEffect(() => {
    let isActive = true;

    setIsLoading(true);

    let data: any[] | null = null;

    const initData = async () => {
      try {
        const groupId = selectedGroup.id || undefined;
        const levelId = !groupId && !isTeacher ? user?.progress?.userLevel || 1 : undefined;
        const userId = !groupId ? user?.id : undefined;

        data = await Promise.all([
          EllReport.getMistakes({
            groupId: groupId,
            levelId: levelId,
            userId: userId,
            orderBy: groupId ? 'count' : 'quizzes',
            isOrderDESC: true,
          }),
        ]);
      } catch (ex) {}

      if (!isActive) {
        return;
      }

      if (data) {
        setMistakes(data[0] as GetMistakesResponse[]);
      }

      setIsLoading(false);
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [selectedGroup, user, isTeacher]);

  const openQuiz = useCallback(
    (enhancementArea: string, enhancementAreaSimplified: string) => {
      const groupId = selectedGroup.id || undefined;
      const levelId = !groupId && !isTeacher ? user?.progress?.userLevel || 1 : undefined;
      const userId = !groupId ? user?.id : undefined;

      navigate('/dashboard/my-tutor-chat', {
        state: {
          type: PrivateTutorTypes.QUIZ,
          enhancementArea: enhancementArea,
          enhancementAreaSimplified: enhancementAreaSimplified,
          userId: userId,
          groupId: groupId,
          levelId: levelId,
        },
      });
    },
    [navigate, selectedGroup, isTeacher, user]
  );

  const mistakesBlock = useMemo(() => {
    const showLearning = !selectedGroup.id;

    const mistakesList = showLearning
      ? mistakes
          .sort((m1: GetMistakesResponse, m2: GetMistakesResponse) =>
            TutorTopics.sortDescending(
              m1.numberOfQuizzes - m1.numberOfDoneQuizzes,
              m2.numberOfQuizzes - m2.numberOfDoneQuizzes
            )
          )
      : mistakes;

    if (mistakesList.length === 0) {
      return;
    }

    return (
      <>
        <div>
          <h2 className={classNames.commonMistakeTitle}>
            {t(showLearning ? 'my_tutor_page.letsPractice' : 'my_tutor_page.commonMistakes')}
          </h2>
          <div className={classNames.commonMistakeTitleDescription}>
            {t('my_tutor_page.commonMistakeDescription')}
          </div>
        </div>
        <CommonMistakes mistakes={mistakesList} showLearning={showLearning} onSelect={openQuiz} />
      </>
    );
  }, [t, mistakes, selectedGroup, openQuiz]);
  */
